/**
 * ARQUIVO CORRIGIDO (RESOLVE ERRO DE LOGIN)
 * Caminho: src/contexts/AuthContext.js
 * Descrição: O provedor do contexto foi ajustado para renderizar os componentes
 * filhos apenas após o término do carregamento (loading), prevenindo o erro que
 * ocorria quando outros componentes tentavam acessar 'userData' antes de ele
 * estar definido.
 */
import React, { createContext, useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import { requestNotificationPermission } from '../firebase/messaging';

export const AuthContext = createContext();

const KNOWN_ADMINS = {
    'fabiocarmopsi@gmail.com': {
        name: 'Fábio de Moraes Carmo',
        role: 'Administrador'
    },
    'alicecarmopsi@gmail.com': {
        name: 'Alice Gonçalves Santos Carmo',
        role: 'Administrador'
    }
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (userData?.preferences?.themeColor) {
            const hex = userData.preferences.themeColor;
            // Convert Hex to RGB
            let r = 0, g = 0, b = 0;
            if (hex.length === 4) {
                r = parseInt("0x" + hex[1] + hex[1]);
                g = parseInt("0x" + hex[2] + hex[2]);
                b = parseInt("0x" + hex[3] + hex[3]);
            } else if (hex.length === 7) {
                r = parseInt("0x" + hex[1] + hex[2]);
                g = parseInt("0x" + hex[3] + hex[4]);
                b = parseInt("0x" + hex[5] + hex[6]);
            }
            document.documentElement.style.setProperty('--color-brand-core', `${r} ${g} ${b}`);
        } else {
            // Revert to default if no preference
            document.documentElement.style.removeProperty('--color-brand-core');
        }
    }, [userData]);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (authUser) => {
            setUser(authUser);
            if (authUser && authUser.isAnonymous) {
                // Login anônimo (usado só pelos links públicos de teste): NUNCA é profissional nem paciente
                // e não dá acesso ao sistema. Antes caía no "fallback" como Profissional.
                setUserData({ uid: authUser.uid, role: 'Anonimo', isAnonymous: true, name: 'Visitante' });
                setLoading(false);
                return;
            }
            if (authUser) {
                setLoading(true);
                const emailLower = (authUser.email || '').toLowerCase().trim();
                const isKnownAdmin = Boolean(KNOWN_ADMINS[emailLower]);

                // Se for um administrador principal conhecido (Dr. Fábio ou Dra. Alice),
                // inicializa imediatamente como Administrador para NUNCA cair em falso "SemAcesso"
                if (isKnownAdmin) {
                    setUserData({
                        uid: authUser.uid,
                        email: authUser.email,
                        name: KNOWN_ADMINS[emailLower].name || authUser.displayName || 'Administrador',
                        role: 'Administrador'
                    });
                }

                const userDocRef = doc(db, "users", authUser.uid);

                // Busca ativa direta no Firestore (evita travamento se o cache offline estiver vazio/limpo)
                getDoc(userDocRef).then((directSnap) => {
                    if (directSnap.exists()) {
                        const directData = { uid: directSnap.id, ...directSnap.data() };
                        setUserData(prev => ({ ...prev, ...directData }));
                        setLoading(false);
                    }
                }).catch(() => {});

                let unsubscribePatient = null;
                const unsubscribeUser = onSnapshot(userDocRef, (docSnapshot) => {
                    if (docSnapshot.exists()) {
                        const data = { uid: docSnapshot.id, ...docSnapshot.data() };
                        setUserData(prev => ({ ...prev, ...data }));
                        setLoading(false);

                        if (!data.fcmToken) {
                            requestNotificationPermission(data);
                        }
                        return;
                    }

                    // Se o snapshot veio de um cache local vazio (ex.: após limpar dados do navegador),
                    // NÃO desista nem encerre o listener! O servidor ainda vai responder.
                    if (docSnapshot.metadata?.fromCache) {
                        return;
                    }

                    // Se for admin conhecido, preserva o papel mesmo que o documento demore
                    if (isKnownAdmin) {
                        setLoading(false);
                        return;
                    }

                    // O servidor confirmou que não existe em /users -> verifica se é paciente
                    const patientDocRef = doc(db, "patients", authUser.uid);
                    if (unsubscribePatient) unsubscribePatient();
                    unsubscribePatient = onSnapshot(patientDocRef, (patientDocSnapshot) => {
                        if (patientDocSnapshot.exists()) {
                            const data = { uid: patientDocSnapshot.id, role: 'Paciente', ...patientDocSnapshot.data() };
                            setUserData(data);
                            setLoading(false);

                            if (!data.fcmToken) {
                                requestNotificationPermission(data);
                            }
                            return;
                        }

                        // Se veio de cache vazio, aguarda a resposta do servidor
                        if (patientDocSnapshot.metadata?.fromCache) {
                            return;
                        }

                        // Servidor confirmou que não existe nem em users nem em patients
                        setUserData({
                            uid: authUser.uid,
                            email: authUser.email,
                            name: authUser.displayName || authUser.email?.split('@')[0] || 'Usuário',
                            role: 'SemAcesso',
                            isUnregistered: true
                        });
                        setLoading(false);
                    });
                }, (error) => {
                    console.error("Erro ao buscar dados do usuário:", error);
                    if (isKnownAdmin) {
                        setLoading(false);
                        return;
                    }
                    setUserData({
                        uid: authUser.uid,
                        email: authUser.email,
                        name: authUser.displayName || authUser.email?.split('@')[0] || 'Usuário',
                        role: 'SemAcesso',
                        isUnregistered: true,
                        accessCheckFailed: true
                    });
                    setLoading(false);
                });

                return () => {
                    unsubscribeUser();
                    if (unsubscribePatient) unsubscribePatient();
                };
            } else {
                setUserData(null);
                setLoading(false);
            }
        });
        return () => unsubscribe();
    }, []);

    const value = { user, userData, loading };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};